// Purchasing API client — pembungkus /api/purchasing/*, /api/pdf/*,
// /api/notifications/*.
//
// Cakupan: SELURUH 59 endpoint purchasing + 4 PDF Archive + 3 notifikasi.
// Disinkronkan per 2026-08-09 (#4b-3c) dengan backend/routers/purchasing.py,
// pdf_archive.py, notifications.py.
//
// Sejak #4b-3c tidak ada lagi endpoint purchasing yang belum dibungkus: 11
// terakhir (GET /meta/dp + 10 endpoint /dp/*) masuk bersama dua layar uang
// muka yang memakainya. Endpoint ke-59 adalah POST /dp/{id}/upload-proof —
// BARU, lahir karena jalur unggah bukti DP ternyata buntu: /pay mewajibkan
// proof_file_id, tapi satu-satunya endpoint unggah saat itu mensyaratkan
// invoice yang pada uang muka sering belum ada.
//
// Aturan yang tetap berlaku untuk penambahan berikutnya: pembungkus dibuat
// bersama layar yang memakainya, bukan di muka — pembungkus tanpa pemakai
// hanya jadi kode mati yang menua diam-diam.
//
// ATURAN FILE INI
//   * Client TIDAK menjaga apa pun. Setiap role guard, state machine, batas
//     toleransi, dan batas approval ditegakkan backend. Fungsi di sini hanya
//     memanggil — komentar role di bawah adalah CATATAN, bukan penegakan.
//   * Jangan menyalin konstanta bisnis ke sini (toleransi over-receive,
//     batas approval region, daftar status). Baca dari respons API.
import { api } from "./api";

// ---------- Vendor ----------
// Write: OWNER, ADMIN_PURCHASING. Delete: OWNER saja.
export const listVendors = (params) => api.get("/purchasing/vendors", { params });
export const getVendor = (id) => api.get(`/purchasing/vendors/${id}`);
export const createVendor = (body) => api.post("/purchasing/vendors", body);
export const updateVendor = (id, body) => api.patch(`/purchasing/vendors/${id}`, body);
export const deleteVendor = (id) => api.delete(`/purchasing/vendors/${id}`);

// ---------- Purchasing Request ----------
// State: DRAFT → SUBMITTED → APPROVED / REJECTED / CANCELLED
//
// Routing approval (Fase 2a) — approver ditentukan role PEMOHON, bukan
// sekadar role penyetuju:
//   PR dari ADMIN_GUDANG   → hanya KEPALA_CABANG region yang sama
//   PR dari KEPALA_CABANG  → hanya OWNER
// Requester tidak boleh approve PR sendiri; Owner pun tidak bypass.
//
// Batas approval (migration 025): PR yang nilainya DI ATAS
// regions.pr_approval_limit_idr hanya boleh disetujui OWNER. Angkanya per
// region — baca dari data region, jangan ditulis di kode frontend.
//
// Harga wajib saat submit: PR tidak bisa meninggalkan DRAFT kalau ada item
// tanpa estimated_unit_price (backend → 400 PR_ITEM_PRICE_REQUIRED).
export const listPRs = (params) => api.get("/purchasing/pr", { params });
export const getPR = (id) => api.get(`/purchasing/pr/${id}`);
export const createPR = (body) => api.post("/purchasing/pr", body);
export const updatePRHeader = (id, body) => api.patch(`/purchasing/pr/${id}`, body);
export const patchPRItems = (id, body) => api.patch(`/purchasing/pr/${id}/items`, body);
export const submitPR = (id) => api.post(`/purchasing/pr/${id}/submit`);
export const approvePR = (id) => api.post(`/purchasing/pr/${id}/approve`);
export const rejectPR = (id, body) => api.post(`/purchasing/pr/${id}/reject`, body);
export const cancelPR = (id, body) => api.post(`/purchasing/pr/${id}/cancel`, body);

// ---------- Purchase Order ----------
// PO LAHIR OTOMATIS 1:1 saat PR di-APPROVED, berstatus PENDING_VENDOR tanpa
// vendor. Endpoint manual POST /purchasing/po DIHAPUS TOTAL di Fase 2b —
// membiarkannya berarti membuka jalan tikus untuk melanggar aturan 1 PR =
// 1 PO. Karena itu tidak ada createPO di client ini, dan tidak boleh
// ditambahkan kembali.
//
// State: PENDING_VENDOR → ISSUED → PARTIAL_RECEIVED → FULLY_RECEIVED →
//        CLOSED, atau CANCELLED.
// CLOSED terminal keras; PO dengan invoice aktif belum lunas ditolak ditutup
// (PO_CLOSE_BLOCKED_UNPAID).
// Aksi tulis: OWNER, ADMIN_PURCHASING.
/**
 * Daur hidup PO + wewenang pengguna ini atas PO.
 * → { statuses: [{ code, label, description, flow, tone }],
 *     permissions: { read, assign_vendor, edit_items, close, cancel } }
 *
 * Dibaca, TIDAK disalin. Daftar status dan tuple role hidup di backend;
 * menyalinnya ke JS berarti UI berbohong diam-diam begitu backend berubah.
 */
export const getPOMeta = () => api.get("/purchasing/meta/po");

// Respons PO membawa identitas yang sudah di-resolve — `vendor.name`,
// `pr.pr_number`, `region.name`, `store.name` — supaya halaman tidak
// menampilkan UUID dan tidak memanggil API sekali per baris.
export const listPOs = (params) => api.get("/purchasing/po", { params });
export const getPO = (id) => api.get(`/purchasing/po/${id}`);

/**
 * Menetapkan vendor + harga → PENDING_VENDOR menjadi ISSUED.
 * Inilah langkah yang menghidupkan PO; tanpa ini PO tidak bisa diterima
 * barangnya. Hak: OWNER, ADMIN_PURCHASING (lintas region).
 *
 * body: { vendor_id, items?: [{ po_item_id, unit_price }],
 *         expected_delivery_date?, notes? }
 * items opsional — kalau dikosongkan, harga estimasi dari PR dipakai apa adanya.
 */
export const assignPOVendor = (id, body) =>
  api.post(`/purchasing/po/${id}/assign-vendor`, body);

export const patchPOItems = (id, body) => api.patch(`/purchasing/po/${id}/items`, body);
export const closePO = (id) => api.post(`/purchasing/po/${id}/close`);
// Inventaris A20 — PR baru berisi sisa qty PO yang belum diterima (alur PR biasa).
export const buatPRSisa = (id) => api.post(`/purchasing/po/${id}/pr-sisa`);
// BUSINESS-35: tutup sebagian PO — Admin Purchasing mengajukan, KC region memutus.
export const daftarTutupSebagian = (id) => api.get(`/purchasing/po/${id}/tutup-sebagian`);
export const ajukanTutupSebagian = (id, alasan) => api.post(`/purchasing/po/${id}/tutup-sebagian`, { alasan });
export const putusTutupSebagian = (tid, setuju, alasan) =>
  api.post(`/purchasing/po/tutup-sebagian/${tid}/putus`, { setuju, alasan });
export const batalkanTutupSebagian = (tid) => api.post(`/purchasing/po/tutup-sebagian/${tid}/batalkan`);
// BUSINESS-53: vendor telat tanpa uang muka — Admin Purchasing mengajukan batal (lewat 7 hari kerja), KC region memutus.
export const daftarPOTelat = () => api.get(`/purchasing/po-telat`);
export const keterlambatanVendor = (params) => api.get(`/purchasing/vendor-telat`, { params });
export const daftarBatalTelat = (id) => api.get(`/purchasing/po/${id}/batal-telat`);
export const ajukanBatalTelat = (id, alasan) => api.post(`/purchasing/po/${id}/batal-telat`, { alasan });
// K203 BUSINESS-57: pembatalan PO yang sudah dikirim ke vendor — diajukan Admin Purchasing, diputus Kepala Cabang
// lewat putusBatalTelat (mesin pengajuan yang sama).
export const ajukanBatalPO = (id, alasan) => api.post(`/purchasing/po/${id}/pengajuan-batal`, { alasan });
export const putusBatalTelat = (bid, setuju, alasan) =>
  api.post(`/purchasing/po/batal-telat/${bid}/putus`, { setuju, alasan });
export const batalkanBatalTelat = (bid) => api.post(`/purchasing/po/batal-telat/${bid}/batalkan`);
export const cancelPO = (id, body) => api.post(`/purchasing/po/${id}/cancel`, body);

// ---------- GRN / penerimaan barang ----------
// Tulis: OWNER, ADMIN_GUDANG. Baca: + KEPALA_CABANG, ADMIN_PURCHASING,
// ADMIN_PRODUKSI, CS.
//
// qty_match_status ('SESUAI' | 'KURANG' | 'LEBIH') WAJIB di SETIAP item, di
// SETIAP event penerimaan — termasuk partial delivery. Nilainya divalidasi
// ketat terhadap hitungan sistem; yang tidak cocok ditolak.
//
// Penerimaan melebihi toleransi over-receive ditolak
// (OVER_RECEIVE_BLOCKED). Persentasenya milik backend — baca dari field
// `tolerance_pct` pada respons error, jangan ditulis ulang di frontend.
/**
 * Status GRN, arti tiap kesesuaian jumlah, toleransi, dan wewenang pengguna ini.
 * → { statuses, qty_match, tolerance_pct, permissions:{read,receive,cancel} }
 */
export const getGRNMeta = () => api.get("/purchasing/meta/grn");

/**
 * Hitung akibat sebuah penerimaan SEBELUM dijalankan. Baca saja, nol tulisan.
 * body: { po_id, items?: [{ po_item_id, qty_received? }] }
 *
 * Tanpa `items` → tiap item PO dianggap diterima sebanyak SISANYA. Itulah
 * dasar "kalau sesuai PO, tinggal klik".
 *
 * Balikannya membawa, per item: qty_ordered, qty_received_before,
 * qty_outstanding, qty_max_receivable_now, qty_difference, dan
 * **qty_match_status yang dihitung backend**. Frontend TIDAK BOLEH menghitung
 * sendiri status itu — aturannya milik backend dan create_grn menolak nilai
 * yang tidak cocok dengan hitungannya.
 */
export const previewGRN = (body) => api.post("/purchasing/grn/preview", body);

export const listGRNs = (params) => api.get("/purchasing/grn", { params });
export const getGRN = (id) => api.get(`/purchasing/grn/${id}`);
/** body: { po_id, items: [{ po_item_id, qty_received, qty_match_status,
 *          unit_price?, notes?, qty_match_notes? }], receipt_date?, notes? } */
export const createGRN = (body) => api.post("/purchasing/grn", body);
export const cancelGRN = (id, body) => api.post(`/purchasing/grn/${id}/cancel`, body);

// ---------- Invoice / pengajuan pembayaran ----------
// Tulis: ADMIN_PURCHASING SAJA — Owner SENGAJA tidak ikut membuat invoice
// (pemisahan tugas, sama seperti Owner dilarang mengajukan PR).
// Menyetujui/menolak di gerbang: OWNER + KEPALA_CABANG. Menolak saat eksekusi:
// FINANCE. Baca: OWNER, KEPALA_CABANG, ADMIN_PURCHASING, FINANCE.
//
// GERBANG APPROVAL Q48 (#4a) — alur di bawah ini BERUBAH sejak komentar lama
// file ini ditulis (2026-08-01). Yang berlaku sekarang:
//   create   → PENDING_APPROVAL   (bukan langsung UNPAID)
//   approve  → UNPAID             (KC sampai batas region, di atasnya Owner)
//   resubmit → PENDING_APPROVAL   (bukan UNPAID — nominal baru wajib dilihat
//                                  penyetuju lagi)
// Batas nilainya regions.payment_approval_limit_idr — baca dari
// /meta/invoice.approval, JANGAN ditulis di kode frontend.
//
// due_date NULL = cash (bayar langsung); terisi = termin.
// REJECTED BUKAN terminal — perbaikan terjadi di baris yang sama lewat
// resubmit, dan riwayat penolakan dipertahankan.
/**
 * Daur hidup invoice + aturan bisnis + wewenang pengguna ini.
 * → { statuses, derived_statuses, transitions, permissions,
 *     approval:{limit_idr,limit_is_set,...}, overdue_rule,
 *     po_statuses_eligible, business_rules }
 *
 * Dibaca, TIDAK disalin — sama seperti getPOMeta.
 */
export const getInvoiceMeta = () => api.get("/purchasing/meta/invoice");

export const listInvoices = (params) => api.get("/purchasing/invoices", { params });
export const getInvoice = (id) => api.get(`/purchasing/invoices/${id}`);
/** body: { po_id, invoice_date, total_amount, invoice_number?, due_date?, notes? } */
export const createInvoice = (body) => api.post("/purchasing/invoices", body);
export const updateInvoice = (id, body) => api.patch(`/purchasing/invoices/${id}`, body);
export const cancelInvoice = (id, body) => api.post(`/purchasing/invoices/${id}/cancel`, body);
/** K219 / BUSINESS-94: Admin Purchasing mengajukan pembatalan tagihan yang sudah dipotong. body: { alasan }. */
export const ajukanPembatalanInvoice = (id, body) => api.post(`/purchasing/invoices/${id}/pembatalan`, body);
/** K219: Kepala Cabang memutus pengajuan pembatalan. body: { setuju, alasan } — alasan wajib bila menolak. */
export const putusPembatalanInvoice = (pid, body) => api.post(`/purchasing/invoices/pembatalan/${pid}/putus`, body);
/** KC/Owner menyetujui di gerbang (Q48): PENDING_APPROVAL → UNPAID.
 *  Pemohon tidak boleh menyetujui pengajuannya sendiri; di atas batas region
 *  wajib Owner. Keduanya ditegakkan backend. */
export const approveInvoice = (id) => api.post(`/purchasing/invoices/${id}/approve`);
/** KC/Owner MENOLAK di gerbang (Q48). Beda dari rejectInvoice yang dipakai
 *  Finance saat eksekusi. body: { reason } — minimal 3 karakter. */
export const rejectApprovalInvoice = (id, body) =>
  api.post(`/purchasing/invoices/${id}/reject-approval`, body);
/** Finance menolak saat eksekusi. body: { reason } — minimal 3 karakter. */
export const rejectInvoice = (id, body) => api.post(`/purchasing/invoices/${id}/reject`, body);
/** Admin Purchasing memperbaiki + mengajukan ulang yang REJECTED. */
export const resubmitInvoice = (id, body) => api.post(`/purchasing/invoices/${id}/resubmit`, body);

// ---------- Payment ----------
// Tulis: FINANCE SAJA. Owner TIDAK punya override — ini pengecualian Owner
// kedua di sistem ini setelah PR_WRITE. Baca: OWNER, KEPALA_CABANG,
// ADMIN_PURCHASING, FINANCE.
// Overpayment dijaga backend (total bayar tidak boleh melebihi nilai invoice).
//
// SISA TAGIHAN bukan total − paid. Rumus resminya ada di
// /meta/payment.remaining_formula dan memuat suku dp_applied_amount. Tanpa
// suku itu, sisa terhitung terlalu besar dan nominal yang dikirim Finance
// pasti kena OVERPAYMENT_BLOCKED — kegagalan yang tidak dimengerti pengguna
// karena angkanya "sudah sesuai layar".
/**
 * Status pembayaran + metode + aturan bukti transfer + wewenang pengguna ini.
 * → { statuses, payment_methods, transitions, permissions,
 *     proof:{required,max_bytes,allowed_mime_types,signed_url_expires_in_sec,...},
 *     invoice_statuses_payable, invoice_unpayable_reasons, remaining_formula }
 *
 * Dibaca, TIDAK disalin — sama seperti getPOMeta/getInvoiceMeta. Blok `proof`
 * adalah SATU-SATUNYA sumber batas ukuran & daftar MIME di frontend.
 */
export const getPaymentMeta = () => api.get("/purchasing/meta/payment");

export const listPayments = (params) => api.get("/purchasing/payments", { params });
/** Satu pembayaran + blok `proof` berisi signed URL berumur pendek. Dipanggil
 *  ON-DEMAND saat bukti mau dibuka — daftar sengaja TIDAK ikut menerbitkan
 *  signed URL (50 baris = 50 panggilan storage). */
export const getPayment = (id) => api.get(`/purchasing/payments/${id}`);
/**
 * Unggah bukti transfer DULU, catat pembayaran KEMUDIAN — dua langkah, dan
 * itu disengaja backend: kegagalan unggah tidak boleh menggagalkan pembayaran
 * yang datanya sudah benar.
 * @param {File} file
 * @param {string} invoiceId WAJIB — dari sinilah region berkas diturunkan.
 * → { proof_file_id, object_key, filename, size_bytes, mime_type,
 *     signed_url, expires_in_sec }
 */
export const uploadPaymentProof = (file, invoiceId) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("invoice_id", invoiceId);
  return api.post("/purchasing/payments/upload-proof", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
/** body: { invoice_id, payment_date, amount, payment_method, proof_file_id,
 *          bank_ref?, notes? } — proof_file_id dari uploadPaymentProof. */
export const createPayment = (body) => api.post("/purchasing/payments", body);
export const cancelPayment = (id, body) => api.post(`/purchasing/payments/${id}/cancel`, body);

// ---------- Uang muka vendor (DP) ----------
// Ajukan: ADMIN_PURCHASING. Setujui/tolak: OWNER + KEPALA_CABANG (KC sampai
// batas region, di atasnya WAJIB Owner). Bayar/potongkan/selesaikan: FINANCE.
// Hapus buku: OWNER SAJA.
//
// State: PENDING_APPROVAL → APPROVED → PAID → APPLIED,
//        atau REJECTED / CANCELLED / CREDITED / WRITTEN_OFF.
//
// TIGA HAL YANG WAJIB DIBACA DARI /meta/dp, JANGAN DITULIS DI SINI:
//   1. `active_statuses` (status yang menahan plafon 50% PO) memuat
//      WRITTEN_OFF tapi TIDAK memuat PENDING_APPROVAL. Asimetri itu
//      keputusan sadar Owner (Q58), bukan kelalaian — write-off mengesahkan
//      kerugian, bukan menghapus uang yang sudah keluar.
//   2. Ambang panjang alasan BERBEDA per aksi: 10 karakter untuk pengajuan
//      dan hapus buku, 3 untuk tolak/batal/selesaikan. Baca dari
//      transitions[].min_reason_length.
//   3. `limits.cap_ratio` — rasio plafon terhadap nilai PO.
/**
 * Daur hidup uang muka (8 status) + plafon + kelayakan + wewenang pengguna ini.
 * → { statuses, active_statuses, transitions, settlement_types,
 *     payment_methods, proof, limits, eligibility, approval, permissions }
 *
 * Dibaca, TIDAK disalin — sama seperti getPOMeta/getInvoiceMeta.
 */
export const getDPMeta = () => api.get("/purchasing/meta/dp");

export const listDPs = (params) => api.get("/purchasing/dp", { params });
export const getDP = (id) => api.get(`/purchasing/dp/${id}`);
/** body: { po_id, amount, dp_date, request_reason, notes? }
 *  request_reason minimal 10 karakter (Q60) — angkanya dari meta. */
export const createDP = (body) => api.post("/purchasing/dp", body);
/** KC sampai batas region; di atasnya wajib Owner. Pemohon tidak boleh
 *  menyetujui pengajuannya sendiri. Keduanya ditegakkan backend. */
export const approveDP = (id) => api.post(`/purchasing/dp/${id}/approve`);
export const rejectDP = (id, body) => api.post(`/purchasing/dp/${id}/reject`, body);
/** Hanya selagi uang belum keluar. Yang sudah dibayar diselesaikan lewat
 *  settleDP atau dihapusbukukan lewat writeOffDP. */
export const cancelDP = (id, body) => api.post(`/purchasing/dp/${id}/cancel`, body);
/** body: { payment_method, proof_file_id, bank_ref?, paid_date? }
 *  proof_file_id dari uploadDPProof — WAJIB, dijaga service DAN CHECK DB. */
export const payDP = (id, body) => api.post(`/purchasing/dp/${id}/pay`, body);
/** JALUR UTAMA: dipotongkan PENUH ke satu tagihan PO yang sama (Q61).
 *  body: { invoice_id } */
export const applyDP = (id, body) => api.post(`/purchasing/dp/${id}/apply`, body);
/** body: { settlement_type, reason, credited_to_po_id? }
 *  CREDIT_NEXT_PO WAJIB menyebut PO tujuan; REFUND wajib TIDAK. Pasangan dua
 *  arah itu constraint DB, bukan anjuran. PO tujuan tidak boleh PO milik uang
 *  muka itu sendiri. */
export const settleDP = (id, body) => api.post(`/purchasing/dp/${id}/settle`, body);
/** OWNER SAJA — mengakui uang muka sebagai KERUGIAN. body: { reason }
 *  minimal 10 karakter. Uangnya TETAP menahan plafon PO (Q58). */
export const writeOffDP = (id, body) => api.post(`/purchasing/dp/${id}/write-off`, body);

// Bukti transfer DP punya ENDPOINT SENDIRI, bukan menumpang
// /payments/upload-proof. Endpoint pembayaran mewajibkan invoice_id sebagai
// sumber region, sementara uang muka dibayar SEBELUM ada invoice — PO-nya
// sering belum punya satu pun. Jangan "menyatukan" keduanya di sini: yang
// terlihat mirip di frontend adalah dua penurunan region yang berbeda di
// backend.
//
// Aturan berkasnya (batas ukuran, daftar MIME, umur signed URL) dibaca dari
// /meta/dp.proof — SAMA persis dengan /meta/payment.proof karena mekanikanya
// memang satu fungsi bersama di backend. Tetap dibaca dari meta, tidak
// ditulis di sini.
/**
 * Unggah bukti transfer uang muka, kembalikan proof_file_id untuk /pay.
 * Hak: FINANCE (sama dengan yang mengeksekusi pembayaran).
 * @param {string} id  id DP — pemilik berkas, bukan sekadar konteks.
 * @param {File} file
 * → { proof_file_id, object_key, filename, size_bytes, mime_type,
 *     signed_url, expires_in_sec, dp_id, dp_number }
 */
export const uploadDPProof = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/purchasing/dp/${id}/upload-proof`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// ---------- Retur vendor ----------
// Tulis: OWNER, ADMIN_GUDANG. Guard stok negatif berlaku di jalur ini dan
// OWNER PUN TIDAK BISA BYPASS.
/**
 * Status retur, akibatnya pada stok, dan wewenang pengguna ini.
 *
 * ⛔ Layar retur NOL BOLEH menanam daftar status sendiri. Retur sempat jadi
 * satu-satunya sumber daya purchasing tanpa `meta`, dan itu memaksa UI
 * menebak — konstanta bisnis yang disalin ke UI membuat layar berbohong
 * diam-diam begitu backend-nya berubah.
 */
export const getReturnsMeta = () => api.get("/purchasing/meta/returns");

export const listReturns = (params) => api.get("/purchasing/returns", { params });
export const getReturn = (id) => api.get(`/purchasing/returns/${id}`);
/** body: { po_id, items: [{ po_receipt_item_id, qty_returned, notes? }],
 *          reason, notes? } — reason minimal 3 karakter. */
export const createReturn = (body) => api.post("/purchasing/returns", body);
export const cancelReturn = (id, body) => api.post(`/purchasing/returns/${id}/cancel`, body);
// K197 butir 3: penyelesaian retur sesudah ada tagihan (AP pilih, KC putus).
export const ajukanPenyelesaianRetur = (id, body) => api.post(`/purchasing/returns/${id}/penyelesaian`, body);
export const putusPenyelesaianRetur = (id, body) => api.post(`/purchasing/returns/${id}/penyelesaian/putus`, body);

// ---------- PDF Archive ----------
// Berbeda prefix: /api/pdf, bukan /api/purchasing.
// DESIGNER_EDITOR blanket 403 di sini. FINANCE read-only.
// File di bucket privat — tautannya HARUS lewat signed URL berumur pendek,
// jangan pernah menyimpan/menyebar URL-nya.
export const listDocuments = (params) => api.get("/pdf/documents", { params });
export const getDocument = (id) => api.get(`/pdf/documents/${id}`);
export const getDocumentSignedUrl = (id) => api.get(`/pdf/documents/${id}/signed-url`);
/** body: { kind, related_entity_id, force? } — kind mis. 'PR', 'PO', 'GRN'. */
export const generateDocument = (body) => api.post("/pdf/generate", body);

// ---------- Notifikasi tugas tertunda ----------
// Prefix /api/notifications. Penyaringan region + role dikerjakan backend.
export const listNotifications = (params) => api.get("/notifications", { params });
export const resolveNotification = (id, body) => api.post(`/notifications/${id}/resolve`, body);
// ⛔ `dismissNotification` DICABUT 27 Agustus 2026 bersama endpointnya.
// Notifikasi tugas tidak boleh hilang tanpa dikerjakan (keputusan Owner).
// Yang tersisa: `resolveNotification` untuk yang benar-benar selesai, dan
// pemadaman otomatis di backend saat keadaannya berubah.

// ---------- Re-export helper bersama ----------
// apiErrorMessage TIDAK diduplikasi — sumber tunggalnya inventoryApi.js.
// Backend error message ditampilkan verbatim, jangan di-translate/rewrite.
export { apiErrorMessage, listMaterials } from "./inventoryApi";
